import { z } from "zod";
import { ANALYTICS_COMPARE, ANALYTICS_INTERVALS, ANALYTICS_SCREEN_KEYS, APP_DATA_AVAILABILITY, staffAnalyticsAccessSchema, staffAnalyticsWindowQuerySchema, } from "./staff-analytics.js";
// The Explore tab contract (ADR 0017, decision 11; CHT-2029): any metric of one customer by range, interval and split with the previous period,
// the numbers of one selection of time, and the visits of a range. STAFF ONLY and closed: counts, seconds, money in minor units and closed codes.
// No message text, no event property. A number that cannot be read is absent (`null`), never a made-up zero.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const uuid = z.string().regex(UUID);
const isoDateTime = z.iso.datetime({ offset: false });
const count = z.number().int().min(0).max(1_000_000_000);
const seconds = z.number().int().min(0).max(1_000_000_000);
const minor = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const measure = z.number().min(0).max(Number.MAX_SAFE_INTEGER);
export const ANALYTICS_METRIC_NAMES = [
    "sessions",
    "time_in_app",
    "screens_per_session",
    "screen_time",
    "active_days",
    "requests_created",
    "requests_completed",
    "payments_paid",
    "amount_paid",
    "trips_created",
    "saved_items_added",
    "handoffs_requested",
    "customer_messages",
    "notifications_sent",
];
export const ANALYTICS_SPLIT_NAMES = [
    "none",
    "platform",
    "entry",
    "screen",
    "request_type",
];
export const ANALYTICS_METRIC_UNITS = ["count", "seconds", "ratio", "minor_inr"];
export const ANALYTICS_BUCKET_RULES = ["VISIT_START", "RECORD_TIME"];
export const ANALYTICS_PREVIOUS_UNAVAILABLE = ["RETENTION", "NO_PERIOD"];
export const ANALYTICS_VISIT_PLATFORMS = ["ios", "android"];
export const ANALYTICS_VISIT_ENTRIES = [
    "cold_start",
    "resume",
    "deep_link",
    "notification",
];
// --- the queries --------------------------------------------------------------------------------------------------------------------
/** `GET .../metrics/:metric`: the window of every analytics read, plus the interval, the split and the comparison. */
export const staffAnalyticsMetricQuerySchema = staffAnalyticsWindowQuerySchema.safeExtend({
    interval: z.enum(ANALYTICS_INTERVALS).optional(),
    split: z.enum(ANALYTICS_SPLIT_NAMES).default("none"),
    compare: z.enum(ANALYTICS_COMPARE).default("none"),
});
/** `GET .../selection`: the numbers of one window, with the previous period when asked. */
export const staffAnalyticsSelectionQuerySchema = staffAnalyticsWindowQuerySchema.safeExtend({
    compare: z.enum(ANALYTICS_COMPARE).default("none"),
});
const pageCursor = z
    .string()
    .min(1)
    .max(512)
    .regex(/^[A-Za-z0-9_-]+$/u);
/** `GET .../visits`: the window and a keyset page (newest first; default 20, at most 50). */
export const staffAnalyticsVisitsQuerySchema = staffAnalyticsWindowQuerySchema.safeExtend({
    cursor: pageCursor.optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
// --- the metric ---------------------------------------------------------------------------------------------------------------------
const point = z.strictObject({ t: isoDateTime, value: measure, partial: z.boolean() });
export const staffAnalyticsMetricSchema = z.strictObject({
    access: staffAnalyticsAccessSchema,
    metric: z.enum(ANALYTICS_METRIC_NAMES),
    unit: z.enum(ANALYTICS_METRIC_UNITS),
    availability: z.enum(APP_DATA_AVAILABILITY),
    bucketRule: z.enum(ANALYTICS_BUCKET_RULES),
    interval: z.enum(ANALYTICS_INTERVALS),
    split: z.enum(ANALYTICS_SPLIT_NAMES),
    /** One entry per split key (at most 5; `other` holds the rest). Every series has one point per bucket of the window, oldest first. */
    series: z
        .array(z.strictObject({
        key: z.string().min(1).max(40),
        points: z.array(point).max(2000),
    }))
        .max(5),
    /**
     * For a ratio read with a split: the value of each bucket over all series, correctly weighted (two splits at 3 are not 6, and not always 3). `null`
     * for an additive metric and for no split.
     */
    combined: z.array(point).max(2000).nullable(),
    /** The previous period, not split: its points line up with the buckets of the window by position. `null` when there is none. */
    previous: z
        .strictObject({
        from: isoDateTime,
        to: isoDateTime,
        coveredFrom: isoDateTime,
        partial: z.boolean(),
        points: z.array(point).max(2000),
    })
        .nullable(),
    previousUnavailable: z.enum(ANALYTICS_PREVIOUS_UNAVAILABLE).nullable(),
    /** The value over the whole window, and against the previous period (`change` is a fraction: 0.5 is +50%; `null` when it cannot be computed). */
    total: z.strictObject({
        value: measure.nullable(),
        previousValue: measure.nullable(),
        change: z.number().nullable(),
    }),
});
// --- the selection ------------------------------------------------------------------------------------------------------------------
const selectionNumbers = z.strictObject({
    /** `null` for an opted-out customer: the visit read model is never read for them. */
    sessions: count.nullable(),
    timeInAppSeconds: seconds.nullable(),
    requests: count,
    amountPaidMinor: minor,
});
export const staffAnalyticsSelectionSchema = z.strictObject({
    access: staffAnalyticsAccessSchema,
    availability: z.enum(APP_DATA_AVAILABILITY),
    bucketRule: z.literal("VISIT_START"),
    ...selectionNumbers.shape,
    previous: z
        .strictObject({
        from: isoDateTime,
        to: isoDateTime,
        coveredFrom: isoDateTime,
        partial: z.boolean(),
        ...selectionNumbers.shape,
    })
        .nullable(),
    previousUnavailable: z.enum(ANALYTICS_PREVIOUS_UNAVAILABLE).nullable(),
    /** Where the time went, by screen, biggest first. Empty when the customer opted out or no visit is in the selection. */
    whereTimeWent: z
        .array(z.strictObject({ screen: z.enum(ANALYTICS_SCREEN_KEYS), seconds }))
        .max(ANALYTICS_SCREEN_KEYS.length),
});
// --- the visits ---------------------------------------------------------------------------------------------------------------------
export const staffAnalyticsVisitSchema = z.strictObject({
    visitId: uuid,
    startedAt: isoDateTime,
    /** `null` while the visit has not ended (or the app was closed without a last event). */
    endedAt: isoDateTime.nullable(),
    durationSeconds: seconds,
    startInferred: z.boolean(),
    platform: z.enum(ANALYTICS_VISIT_PLATFORMS).nullable(),
    entry: z.enum(ANALYTICS_VISIT_ENTRIES).nullable(),
    screenCount: count,
    eventCount: count,
    /** The records of this customer made while the visit lasted: counts only, never a record. The time overlap is not a cause. */
    records: z.strictObject({ requests: count, payments: count, messages: count }),
});
export const staffAnalyticsVisitsSchema = z.strictObject({
    access: staffAnalyticsAccessSchema,
    availability: z.enum(APP_DATA_AVAILABILITY),
    bucketRule: z.literal("VISIT_START"),
    rows: z.array(staffAnalyticsVisitSchema).max(50),
    nextCursor: pageCursor.nullable(),
});
